import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../contexts/CartContext';
import { API, imgSrc, gbp } from '../lib/shop';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700 mb-1 block">{label}</span>
    {children}
  </label>
);

export default function CheckoutPage() {
  const { items, subtotal } = useCart();
  const [form, setForm] = useState({ name: '', email: '', phone: '', line1: '', line2: '', city: 'Peterborough', postcode: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  if (items.length === 0) return <Navigate to="/cart" replace />;

  const set = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const pay = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/shop/checkout`, {
        items: items.map(i => ({ product_id: i.id, quantity: i.quantity })),
        name: form.name, email: form.email, phone: form.phone,
        address: { line1: form.line1, line2: form.line2, city: form.city, postcode: form.postcode },
        notes: form.notes,
        origin_url: window.location.origin,
      });
      sessionStorage.setItem('pendingOrder', data.order_number);
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not start payment. Please check your details and try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-gray-50" data-testid="checkout-page">
      <div className="container mx-auto px-4 max-w-5xl">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">Checkout</h1>
        <form onSubmit={pay} className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3 space-y-8">
            <section className="bg-white rounded-2xl p-6 border border-gray-100">
              <h2 className="font-bold text-lg text-gray-900 mb-4">Your details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name *"><Input name="name" value={form.name} onChange={set} required data-testid="checkout-name" /></Field>
                <Field label="Email *"><Input type="email" name="email" value={form.email} onChange={set} required data-testid="checkout-email" /></Field>
                <Field label="Phone *"><Input type="tel" name="phone" value={form.phone} onChange={set} required minLength={6} data-testid="checkout-phone" /></Field>
              </div>
            </section>
            <section className="bg-white rounded-2xl p-6 border border-gray-100">
              <h2 className="font-bold text-lg text-gray-900 mb-4">Delivery address</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><Field label="Address line 1 *"><Input name="line1" value={form.line1} onChange={set} required data-testid="checkout-line1" /></Field></div>
                <div className="sm:col-span-2"><Field label="Address line 2"><Input name="line2" value={form.line2} onChange={set} data-testid="checkout-line2" /></Field></div>
                <Field label="Town / City *"><Input name="city" value={form.city} onChange={set} required data-testid="checkout-city" /></Field>
                <Field label="Postcode *"><Input name="postcode" value={form.postcode} onChange={set} required data-testid="checkout-postcode" /></Field>
                <div className="sm:col-span-2"><Field label="Delivery notes (optional)"><Textarea name="notes" value={form.notes} onChange={set} rows={2} data-testid="checkout-notes" /></Field></div>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-2 bg-white rounded-2xl p-6 border border-gray-100 lg:sticky lg:top-28" data-testid="checkout-summary">
            <h2 className="font-bold text-lg text-gray-900 mb-4">Your order</h2>
            <ul className="space-y-3 mb-4 max-h-64 overflow-auto">
              {items.map(i => (
                <li key={i.id} className="flex items-center gap-3 text-sm">
                  <img src={imgSrc(i.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover bg-pink-50" />
                  <span className="flex-1 text-gray-800 leading-snug">{i.name} <span className="text-gray-500">× {i.quantity}</span></span>
                  <span className="font-semibold">{gbp(i.price * i.quantity)}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between text-gray-700 mb-1"><span>Subtotal</span><span>{gbp(subtotal)}</span></div>
            <div className="flex justify-between text-gray-700 mb-3"><span>Delivery</span><span className="text-green-700 font-medium">Free</span></div>
            <div className="flex justify-between font-bold text-lg border-t pt-3 mb-6"><span>Total</span><span data-testid="checkout-total">{gbp(subtotal)}</span></div>
            <Button type="submit" size="lg" disabled={submitting} data-testid="checkout-pay-button" className="w-full bg-pink-500 hover:bg-pink-600 text-white rounded-full">
              <CreditCard className="w-5 h-5 mr-2" /> {submitting ? 'Redirecting to secure payment...' : `Pay ${gbp(subtotal)} securely`}
            </Button>
            <p className="text-xs text-gray-500 mt-3 flex items-center gap-1 justify-center"><Lock className="w-3 h-3" /> Card details are entered on Stripe's secure page. We never see your card number.</p>
            <Link to="/cart" className="block text-center text-sm text-gray-600 hover:text-pink-600 mt-4" data-testid="checkout-back-to-cart">Back to cart</Link>
          </aside>
        </form>
      </div>
    </div>
  );
}
